import Axios from 'axios';

let isRefreshing = false;
let failedQueue = [];
let store = null;
let router = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Setup axios interceptors with store and router injection
export const setupInterceptors = (storeInstance, routerInstance) => {
  store = storeInstance;
  router = routerInstance;
  // Request interceptor - attach token to every request
  Axios.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token && !config.headers['Authorization']) {
        // Only add Authorization if not already set
        config.headers = config.headers || {};
        // Check if token already has Bearer prefix
        config.headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle 401 errors and refresh token
  Axios.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;

      // Skip refresh for login and refresh endpoints
      if (originalRequest.url?.includes('/v2/auth/login') || 
          originalRequest.url?.includes('/v2/auth/refresh')) {
        return Promise.reject(error);
      }

      // Handle 401 Unauthorized and 403 Forbidden errors (expired tokens)
      if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
        if (isRefreshing) {
          // Queue this request while refresh is in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
              return Axios(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          // No refresh token, logout
          isRefreshing = false;
          if (store) store.dispatch('logout');
          if (router) router.push('/');
          return Promise.reject(error);
        }

        try {
          // Call refresh token action
          const response = await store.dispatch('refresh_token');
          // Handle both direct data and nested data structure
          const tokenData = response.data.data || response.data;
          const newToken = tokenData.jwttoken;

          // Update failed requests with new token (with Bearer prefix)
          const tokenWithBearer = newToken.startsWith('Bearer ') ? newToken : `Bearer ${newToken}`;
          processQueue(null, tokenWithBearer);

          // Retry original request with new token
          originalRequest.headers['Authorization'] = tokenWithBearer;
          return Axios(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout user
          processQueue(refreshError, null);
          if (store) store.dispatch('logout');
          if (router) router.push('/');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

export default Axios;
